import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from '../api/client';
import { API } from '../api/config';
import { User, Camera, CheckCircle, CreditCard, AlertCircle, Loader2, FileCheck } from 'lucide-react';
import { CardNumberElement, CardExpiryElement, CardCvcElement, Elements, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { stripeElementStyle } from '../stripeElementStyle';
import {
  emptyExportRefundProfileForm,
  profileResponseToForm,
  ExportRefundProfileForm,
} from '../exportRefund';

const CARD_FEE_GBP = 13;

const stripePk = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_TYooMQauvdEDq54NiTphI7jx';
const stripePromise = loadStripe(stripePk);

// ── £13 Partner Card administration fee form (real Stripe payment,
// rendered inside an <Elements> wrapper) ────────────────────────────────
const CardFeeForm: React.FC<{ userId: string; onPaid: (profileData: any) => void; onCancel: () => void }> = ({ userId, onPaid, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setCardError(null);
    const token = localStorage.getItem('access_token');

    try {
      const intentRes = await axios.post(API.cardFeeIntent(userId), {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { client_secret } = intentRes.data;

      const cardNumber = elements.getElement(CardNumberElement);
      if (!cardNumber) { setCardError('Card element not found. Please refresh and try again.'); setProcessing(false); return; }

      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(client_secret, {
        payment_method: { card: cardNumber },
      });

      if (confirmError) {
        setCardError(confirmError.message || 'Payment failed. Please check your card details.');
        setProcessing(false);
        return;
      }

      if (paymentIntent?.status !== 'requires_capture' && paymentIntent?.status !== 'succeeded') {
        setCardError('Unexpected payment status: ' + paymentIntent?.status + '. Please contact support.');
        setProcessing(false);
        return;
      }

      const payRes = await axios.post(API.payCardFee(userId), {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onPaid(payRes.data);
    } catch (err: any) {
      setCardError(err.response?.data?.detail || err.message || 'Payment error. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handlePay}>
      <div className="form-group">
        <label className="form-label">Card Number</label>
        <div style={{ padding: '12px', background: '#fff', border: '1px solid var(--sidebar-border)', borderRadius: '6px', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
          <CardNumberElement
            options={{ style: stripeElementStyle }}
            onChange={(e) => { setCardComplete(e.complete); setCardError(e.error ? e.error.message : null); }}
          />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
        <div>
          <label className="form-label">Expiry Date</label>
          <div style={{ padding: '12px', background: '#fff', border: '1px solid var(--sidebar-border)', borderRadius: '6px', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
            <CardExpiryElement options={{ style: stripeElementStyle }} />
          </div>
        </div>
        <div>
          <label className="form-label">CVC</label>
          <div style={{ padding: '12px', background: '#fff', border: '1px solid var(--sidebar-border)', borderRadius: '6px', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
            <CardCvcElement options={{ style: stripeElementStyle }} />
          </div>
        </div>
      </div>

      {cardError && (
        <div style={{ color: '#991B1B', fontSize: '13px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', background: '#FEF2F2', padding: '10px', borderRadius: '6px' }}>
          <AlertCircle size={14} /> {cardError}
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px' }}>
        <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={onCancel} disabled={processing}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={!stripe || !cardComplete || processing}>
          {processing ? <><Loader2 className="animate-spin" size={16} /> Processing...</> : `Authorise £${CARD_FEE_GBP} Hold`}
        </button>
      </div>
    </form>
  );
};

const VERIFICATION_DOCS = [
  { id: 'id-photo-input', label: 'ID or Passport', key: 'id_photo_url' as const, type: 'id' as const },
  { id: 'selfie-photo-input', label: 'Identity Selfie', key: 'selfie_photo_url' as const, type: 'selfie' as const },
  { id: 'proof-address-photo-input', label: 'Proof of Address', key: 'proof_address_photo_url' as const, type: 'proof_address' as const },
];

const ApplicantProfile: React.FC = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<ExportRefundProfileForm>(emptyExportRefundProfileForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSection, setSavingSection] = useState<number | null>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  // Four separate declarations required before submission (身分界定 /
  // 用卡須知 / 資料真實 / 隱私權) — every box must be ticked individually.
  const [identityConfirmed, setIdentityConfirmed] = useState(false);
  const [cardTermsAccepted, setCardTermsAccepted] = useState(false);
  const [dataTruthfulConfirmed, setDataTruthfulConfirmed] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showCardFeeModal, setShowCardFeeModal] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      const token = localStorage.getItem('access_token');
      try {
        const response = await axios.get(API.getExportRefundProfile(user.id), {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProfile(profileResponseToForm(response.data));
      } catch (err) {
        console.error('Failed to load profile:', err);
        setError('Failed to load applicant profile.');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user]);

  const isSubmitted = Boolean(profile.application_status && profile.application_status !== 'draft');
  const isApproved = profile.application_status === 'approved';
  const isLocked = isSubmitted && !user?.is_admin;
  const wasRejected = Boolean(profile.risk_freeze_reason && profile.risk_freeze_reason.startsWith('Application rejected'));

  const setField = (field: keyof ExportRefundProfileForm, value: string | boolean) => {
    setProfile((current) => ({ ...current, [field]: value }));
  };

  // Every identity field + every document must be present before the
  // application can be submitted for card issuance review.
  const missingItems = useMemo(() => {
    const missing: string[] = [];
    if (!profile.legal_full_name?.trim()) missing.push('Legal full name');
    if (!profile.date_of_birth?.trim()) missing.push('Birthday');
    if (!profile.contact_phone?.trim()) missing.push('Contact number');
    if (!profile.whatsapp_id?.trim()) missing.push('WhatsApp ID');
    if (!profile.registered_address?.trim()) missing.push('Registered address');
    if (!profile.contact_address?.trim()) missing.push('Contact address');
    if (!profile.id_photo_url) missing.push('ID or Passport photo');
    if (!profile.selfie_photo_url) missing.push('Identity selfie');
    if (!profile.proof_address_photo_url) missing.push('Proof of address');
    return missing;
  }, [profile]);

  const isFormComplete = missingItems.length === 0;
  const allDeclared = identityConfirmed && cardTermsAccepted && dataTruthfulConfirmed && privacyAccepted;

  const saveProfileData = async (section: number): Promise<boolean> => {
    if (!user) return false;
    setSaving(true);
    setSavingSection(section);
    setMessage('');
    setError('');
    const token = localStorage.getItem('access_token');

    try {
      const {
        face_check_completed,
        id_check_completed,
        company_card_requested,
        id_photo_url,
        selfie_photo_url,
        proof_address_photo_url,
        application_status,
        submitted_at,
        approved_at,
        ...updateData
      } = profile;

      const saveResponse = await axios.put(API.saveExportRefundProfile(user.id), updateData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfile(profileResponseToForm(saveResponse.data));
      setMessage('Saved.');
      return true;
    } catch (err) {
      console.error('Failed to save profile:', err);
      setError('Could not save your details. Please try again.');
      return false;
    } finally {
      setSaving(false);
      setSavingSection(null);
    }
  };

  const submitProfile = async () => {
    if (!user || !isFormComplete || !allDeclared) return;
    setMessage('');
    setError('');

    const savedOk = await saveProfileData(4);
    if (!savedOk) return;

    const token = localStorage.getItem('access_token');
    setSaving(true);
    try {
      const response = await axios.post(
        API.submitExportRefundProfile(user.id),
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setProfile(profileResponseToForm(response.data));
      setShowCardFeeModal(true);
      setMessage('Application submitted. Last step: authorise the £13 administration fee hold on your card.');
    } catch (err) {
      console.error('Failed to submit profile:', err);
      setError('Could not submit the application.');
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (
    event: React.ChangeEvent<HTMLInputElement>,
    photoType: 'id' | 'selfie' | 'proof_address',
  ) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    setUploading(photoType);
    setMessage('');
    setError('');

    const token = localStorage.getItem('access_token');
    const formData = new FormData();
    formData.append('photo_type', photoType);
    formData.append('file', file);

    try {
      const uploadRes = await axios.post(API.uploadExportRefundProfilePhoto(user.id), formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setMessage(
        photoType === 'proof_address'
          ? 'Proof of address uploaded successfully.'
          : `${photoType === 'id' ? 'ID/Passport' : 'Selfie'} photo uploaded successfully.`,
      );
      // Merge just the uploaded filename into local state instead of
      // refetching the whole profile — a full refresh would overwrite any
      // Section 1 field the customer had typed but not yet clicked Save
      // for, silently losing their in-progress edits.
      const key = VERIFICATION_DOCS.find((d) => d.type === photoType)!.key;
      setField(key, uploadRes.data.filename);
    } catch (err) {
      console.error('Photo upload failed:', err);
      setError('Failed to upload photo. Please try again.');
    } finally {
      setUploading(null);
      event.target.value = '';
    }
  };

  const handleCardFeePaid = (profileData: any) => {
    setProfile(profileResponseToForm(profileData));
    setMessage(`£${CARD_FEE_GBP} authorised and held on your card. You will only be charged when your application is approved — if it is rejected, the hold is released automatically.`);
    setShowCardFeeModal(false);
  };

  const cardStatusLabel = useMemo(() => {
    if (profile.card_status === 'active') return 'Active';
    if (profile.card_status === 'pending_issuance') return 'Pending Issuance';
    if (profile.card_status === 'legal_escalation') return 'Legal Escalation';
    if (profile.card_status === 'frozen' || profile.card_status === 'violation') return 'Frozen';
    return 'Inactive';
  }, [profile.card_status]);

  const statusLabel = useMemo(() => {
    if (isApproved) return 'Approved';
    if (isSubmitted) return 'Under Review';
    return 'Draft';
  }, [isApproved, isSubmitted]);

  if (loading) return <div>Loading profile...</div>;

  const sectionTitleStyle = { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' } as const;

  return (
    <div>
      <style>{`
        .profile-grid { grid-template-columns: 1fr; }
        .bank-info-grid { grid-template-columns: 1fr; }
        .verification-docs-grid { grid-template-columns: 1fr; }
      `}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', flexWrap: 'wrap', gap: '12px' }}>
        <h1 className="page-title" style={{ margin: 0 }}>Procurement Partner Program Cards Application</h1>
        <div className={`status-pill ${isApproved ? 'status-active' : isSubmitted ? 'status-pending' : 'status-outline'}`}>
          {statusLabel.toUpperCase()}
        </div>
      </div>

      {(message || error) && (
        <div
          style={{
            marginBottom: '24px',
            padding: '16px',
            borderRadius: '8px',
            background: error ? '#FEF2F2' : '#F0FDF4',
            color: error ? '#991B1B' : '#166534',
            border: `1px solid ${error ? '#FCA5A5' : '#BBF7D0'}`,
            fontSize: '14px',
          }}
        >
          {error || message}
        </div>
      )}

      {wasRejected && !isLocked && (
        <div style={{ marginBottom: '24px', padding: '16px', borderRadius: '8px', background: '#FFFBEB', color: '#92400E', border: '1px solid #FDE68A', fontSize: '14px' }}>
          <strong>Previous application was not approved.</strong> Please review your details below, correct anything necessary, and resubmit.
          {profile.risk_freeze_reason && (
            <div style={{ marginTop: '8px', fontSize: '13px' }}>Reason: {profile.risk_freeze_reason.replace('Application rejected: ', '')}</div>
          )}
        </div>
      )}

      {/* ── Section 1: Personal Information ─────────────────────────── */}
      <div className="profile-grid" style={{ display: 'grid', gap: '32px' }}>
        <div className="section-card">
          <div style={sectionTitleStyle}>
            <User size={20} className="text-primary" />
            <h3 className="card-title" style={{ margin: 0 }}>Section 1 · Personal Information</h3>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="applicant-legal-name-input">Legal Full Name *</label>
            <input id="applicant-legal-name-input" className="form-input" value={profile.legal_full_name} onChange={(e) => setField('legal_full_name', e.target.value)} readOnly={isLocked} />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="applicant-dob-input">Birthday *</label>
            <input id="applicant-dob-input" type="date" className="form-input" value={profile.date_of_birth} onChange={(e) => setField('date_of_birth', e.target.value)} readOnly={isLocked} />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="applicant-contact-phone-input">Contact Number *</label>
            <input id="applicant-contact-phone-input" type="tel" className="form-input" value={profile.contact_phone} onChange={(e) => setField('contact_phone', e.target.value)} readOnly={isLocked} />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="applicant-whatsapp-input">WhatsApp ID *</label>
            <input id="applicant-whatsapp-input" className="form-input" placeholder="e.g. +852 9XXX XXXX" value={profile.whatsapp_id} onChange={(e) => setField('whatsapp_id', e.target.value)} readOnly={isLocked} />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="applicant-registered-address-input">Registered Address *</label>
            <textarea
              id="applicant-registered-address-input"
              className="form-input"
              value={profile.registered_address}
              onChange={(e) => setField('registered_address', e.target.value)}
              readOnly={isLocked}
              style={{ minHeight: '100px', resize: 'vertical' }}
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="applicant-contact-address-input">Contact Address *</label>
            <textarea
              id="applicant-contact-address-input"
              className="form-input"
              value={profile.contact_address}
              onChange={(e) => setField('contact_address', e.target.value)}
              readOnly={isLocked}
              style={{ minHeight: '100px', resize: 'vertical' }}
            />
          </div>

          <div style={{ marginTop: '24px' }}>
            <button
              className="btn btn-primary"
              onClick={() => saveProfileData(1)}
              disabled={saving || isLocked}
            >
              {savingSection === 1 ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {/* ── Section 2: Verification Documents ───────────────────────── */}
        <div className="section-card">
          <div style={sectionTitleStyle}>
            <FileCheck size={20} className="text-primary" />
            <h3 className="card-title" style={{ margin: 0 }}>Section 2 · Verification Documents</h3>
          </div>

          <div className="verification-docs-grid" style={{ display: 'grid', gap: '24px' }}>
            {VERIFICATION_DOCS.map((doc) => (
              <div key={doc.type} className="upload-card" style={{ textAlign: 'left', padding: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <span style={{ fontWeight: 600 }}>{doc.label} *</span>
                  {profile[doc.key as keyof ExportRefundProfileForm] && (
                    <CheckCircle size={18} style={{ color: '#10B981' }} />
                  )}
                </div>
                <input
                  type="file"
                  id={doc.id}
                  style={{ display: 'none' }}
                  accept="image/*"
                  disabled={isLocked}
                  onChange={(e) => uploadPhoto(e, doc.type)}
                />
                <button
                  className="btn btn-outline"
                  style={{ width: '100%' }}
                  disabled={uploading === doc.type || isLocked}
                  onClick={() => document.getElementById(doc.id)?.click()}
                >
                  <Camera size={16} />
                  {uploading === doc.type ? 'Uploading...' : 'Upload Photo'}
                </button>
              </div>
            ))}
          </div>

          <div style={{ marginTop: '24px' }}>
            <button
              className="btn btn-primary"
              onClick={() => saveProfileData(2)}
              disabled={saving || isLocked}
            >
              {savingSection === 2 ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {/* ── Section 3: Card Administration Fee (£13) ────────────────── */}
        <div className="section-card">
          <div style={sectionTitleStyle}>
            <CreditCard size={20} className="text-primary" />
            <h3 className="card-title" style={{ margin: 0 }}>Section 3 · Card Administration Fee</h3>
            <div className={`status-pill ${profile.card_status === 'active' ? 'status-active' : 'status-outline'}`}>
              {cardStatusLabel.toUpperCase()}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '20px', marginBottom: '24px' }}>
            <div>
              <div className="form-label">Administration Fee</div>
              <div style={{ fontWeight: 600 }}>£{CARD_FEE_GBP.toFixed(2)}</div>
            </div>
            <div>
              <div className="form-label">Payment Status</div>
              <div style={{ fontWeight: 600 }}>
                {isApproved && profile.card_fee_paid ? 'Captured' : profile.card_fee_paid ? 'Authorised (held)' : 'Unpaid'}
              </div>
            </div>
            <div>
              <div className="form-label">Card Number</div>
              <div style={{ fontWeight: 600 }}>{profile.card_number_last4 ? `•••• ${profile.card_number_last4}` : 'Not issued'}</div>
            </div>
          </div>

          {!isSubmitted ? (
            <p style={{ color: '#64748B', fontSize: '14px', margin: 0 }}>
              When you submit your application, £{CARD_FEE_GBP} will be placed as a pre-authorization hold on your card — no money leaves your account. It is only charged when your application is approved; if rejected, the hold is released automatically.
            </p>
          ) : !profile.card_fee_paid ? (
            <>
              <p style={{ color: '#92400E', fontSize: '14px', marginBottom: '16px' }}>
                Your application is awaiting the £{CARD_FEE_GBP} card authorization. Please authorise the hold to complete your submission.
              </p>
              <button className="btn btn-primary" onClick={() => setShowCardFeeModal(true)}>
                Authorise £{CARD_FEE_GBP} Hold
              </button>
            </>
          ) : !isApproved ? (
            <p style={{ color: '#64748B', fontSize: '14px', margin: 0 }}>
              £{CARD_FEE_GBP} authorised and held on your card. You will only be charged once our team approves your application; if it is not approved, the hold is released and nothing is charged.
            </p>
          ) : profile.card_status === 'active' ? (
            <p style={{ color: '#166534', fontSize: '14px', margin: 0 }}>
              Your Partner Visa Card is active and ready to use.
            </p>
          ) : (
            <p style={{ color: '#166534', fontSize: '14px', margin: 0 }}>
              Application approved and £{CARD_FEE_GBP} fee captured — our team is issuing your card and it will appear here once ready.
            </p>
          )}
        </div>

        {/* ── Section 4: Declarations & Submit ────────────────────────── */}
        {!isLocked && (
          <div className="section-card">
            <div style={sectionTitleStyle}>
              <CheckCircle size={20} className="text-primary" />
              <h3 className="card-title" style={{ margin: 0 }}>Section 4 · Declarations &amp; Submission</h3>
            </div>

            <div style={{ display: 'grid', gap: '16px' }}>
              <label style={{ display: 'flex', gap: '12px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={identityConfirmed}
                  onChange={(e) => setIdentityConfirmed(e.target.checked)}
                  style={{ marginTop: '4px' }}
                />
                <span style={{ fontSize: '14px', color: '#64748B' }}>
                  我確認本人符合此計畫之身分界定（獨立採購代理）。/ I confirm I meet the identity definition of this program — an independent procurement contractor (身分界定).
                </span>
              </label>
              <label style={{ display: 'flex', gap: '12px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={cardTermsAccepted}
                  onChange={(e) => setCardTermsAccepted(e.target.checked)}
                  style={{ marginTop: '4px' }}
                />
                <span style={{ fontSize: '14px', color: '#64748B' }}>
                  我已閱讀並同意用卡須知。/ I have read and agree to the Partner Visa Card usage terms and conditions (用卡須知).
                </span>
              </label>
              <label style={{ display: 'flex', gap: '12px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={dataTruthfulConfirmed}
                  onChange={(e) => setDataTruthfulConfirmed(e.target.checked)}
                  style={{ marginTop: '4px' }}
                />
                <span style={{ fontSize: '14px', color: '#64748B' }}>
                  我聲明以上填寫之個人資料及上傳文件均為真實且正確。/ I declare that all personal information provided and documents uploaded above are true and correct (個人資料填寫真實).
                </span>
              </label>
              <label style={{ display: 'flex', gap: '12px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  style={{ marginTop: '4px' }}
                />
                <span style={{ fontSize: '14px', color: '#64748B' }}>
                  我同意隱私權政策及個人資料使用條款。/ I agree to the Privacy Policy and the Personal Data Usage Terms (隱私權及個人資料使用).
                </span>
              </label>
            </div>

            {!isFormComplete && (
              <div style={{ marginTop: '20px', padding: '14px 18px', borderRadius: '6px', background: '#FFFBEB', border: '1px solid #FDE68A', fontSize: '13px', color: '#92400E' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', fontWeight: 600 }}>
                  <AlertCircle size={14} /> Complete these before submitting:
                </div>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {missingItems.map((item) => <li key={item}>{item}</li>)}
                </ul>
              </div>
            )}

            <div style={{ marginTop: '24px' }}>
              <button
                className="btn btn-primary"
                onClick={submitProfile}
                disabled={saving || isLocked || !allDeclared || !isFormComplete}
              >
                {saving ? 'Submitting...' : 'Submit Application'}
              </button>
              <p style={{ color: '#94A3B8', fontSize: '12px', marginTop: '10px' }}>
                All fields must be completed truthfully with genuine documents — this information is used for card issuance identity verification.
              </p>
            </div>
          </div>
        )}
      </div>

      {showCardFeeModal && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 1000, padding: '20px',
          }}
        >
          <div className="section-card" style={{ maxWidth: '440px', width: '100%', padding: '32px' }}>
            <h3 className="card-title" style={{ marginBottom: '12px' }}>Authorise £{CARD_FEE_GBP} Card Administration Fee</h3>
            <p style={{ color: '#64748B', fontSize: '14px', marginBottom: '24px' }}>
              This £{CARD_FEE_GBP} is a <strong>pre-authorization hold</strong> — no money leaves your account now.
              It will only be charged when your application is approved. If your application is rejected, the
              hold is released automatically and you are never charged.
            </p>
            {user && (
              <Elements stripe={stripePromise}>
                <CardFeeForm userId={user.id} onPaid={handleCardFeePaid} onCancel={() => setShowCardFeeModal(false)} />
              </Elements>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicantProfile;
